<template>
  <div class="bulk-import-content">
    <div class="import-stepper">
      <div class="step" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
        <div class="step-circle">
          <svg v-if="currentStep > 1" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3L4.5 8.5L2 6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span v-else>1</span>
        </div>
        <div class="step-label">Upload Files</div>
      </div>
      <div class="step-line"></div>
      <div class="step" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
        <div class="step-circle">
          <svg v-if="currentStep > 2" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3L4.5 8.5L2 6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span v-else>2</span>
        </div>
        <div class="step-label">Map Fields</div>
      </div>
      <div class="step-line"></div>
      <div class="step" :class="{ active: currentStep === 3 }">
        <div class="step-circle">3</div>
        <div class="step-label">Preview & Import</div>
      </div>
    </div>

    <div class="divider"></div>

    <Step1Upload
      v-if="currentStep === 1"
      :uploadedFiles="uploadedFiles"
      @files-selected="processFiles"
      @remove-file="removeFile"
    />

    <Step2Mapping
      v-if="currentStep === 2"
      :uploadedFiles="uploadedFiles"
      :globalMapping="globalMapping"
      :bomFields="bomFields"
      :showErrors="showValidationErrors"
      @update-mapping="handleUpdateMapping"
      @remove-file="removeFile"
    />

    <Step3Preview
      v-if="currentStep === 3"
      :uploadedFiles="uploadedFiles"
      :globalMapping="globalMapping"
      :bomFields="bomFields"
    />

    <div class="import-actions">
      <button type="button" v-if="currentStep === 1" class="btn-cancel" @click="handleClose" :disabled="isSubmitting">Cancel</button>
      <button type="button" v-if="currentStep > 1" class="btn-cancel btn-back" @click="currentStep--" :disabled="isSubmitting">
        <img
          src="~@/assets/images/icons/chevron-left.svg"
          class="icon-left"
          alt="<"
        />
        Back
      </button>

      <div class="right-actions" style="display: flex; gap: 16px; align-items: center; margin-left: auto;">
        <button type="button" v-if="currentStep === 3" class="btn-cancel" @click="handleCancelStep3" :disabled="isSubmitting">Cancel</button>

        <button type="button" v-if="currentStep === 1" class="btn-next" :class="{ 'active': uploadedFiles.length > 0 }" :disabled="uploadedFiles.length === 0" @click="currentStep = 2">
          Map Fields
          <img
            src="~@/assets/images/icons/chevron-right.svg"
            class="icon-right"
            alt=">"
          />
        </button>

        <button type="button" v-if="currentStep === 2" class="btn-next active" @click="goToStep3">
          Preview
          <img
            src="~@/assets/images/icons/chevron-right.svg"
            class="icon-right"
            alt=">"
          />
        </button>

        <button type="button" v-if="currentStep === 3" class="btn-next active" :class="{ 'is-loading': isSubmitting }" :disabled="isSubmitting" @click="importFiles">
          <svg v-if="isSubmitting" class="spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 4px;">
            <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 4px;">
            <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ isSubmitting ? 'Importing...' : `Import ${uploadedFiles.length} File${uploadedFiles.length > 1 ? 's' : ''}` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>

import Step1Upload from './step1Upload.vue'
import Step2Mapping from './step2Mapping.vue'
import Step3Preview from './step3Preview.vue'
import { generateS3SubmittingUrlMulti, BulkImportBom } from '@/api'
import { mapGetters } from 'vuex'
import { alert, success } from '@/plugins/notification'
import ConfirmationDialog from '@/plugins/confirmationDialog'

export default {
  name: 'BulkImportModalContent',
  components: {
    Step1Upload,
    Step2Mapping,
    Step3Preview
  },
  data () {
    return {
      currentStep: 1,
      isSubmitting: false,
      showValidationErrors: false,
      uploadedFiles: [],
      globalMapping: {
        headerRow: 1,
        mappings: {},
        identificationCells: {},
        detectedColumns: [],
        columnsFound: 0,
        isParsed: false,
        isFullyMapped: false,
        parsedFileId: null
      },
      finalMappingPayload: null,
      bomFields: [
        { id: 'erp_material_id', label: 'ERP Material ID', required: true },
        { id: 'quantity', label: 'Quantity', required: true },
        { id: 'unit_size', label: 'Unit Size', required: false }
      ]
    }
  },
  computed: {
    ...mapGetters(['openTenantId', 'isOnProjectLevel', 'openProjectId'])
  },
  watch: {
    currentStep () {
      this.showValidationErrors = false
    }
  },
  methods: {
    handleClose () {
      this.resetData()
      this.$emit('close')
    },
    resetData () {
      this.currentStep = 1
      this.showValidationErrors = false
      this.uploadedFiles = []
      this.globalMapping = {
        headerRow: 1,
        mappings: {},
        identificationCells: {},
        detectedColumns: [],
        columnsFound: 0,
        isParsed: false,
        isFullyMapped: false,
        parsedFileId: null
      }
      this.finalMappingPayload = null
    },
    handleUpdateMapping (updatedMapping) {
      this.globalMapping = { ...this.globalMapping, ...updatedMapping }
    },
    handleCancelStep3 () {
      ConfirmationDialog(
        'Are you sure you want to cancel the import?',
        (res) => {
          if (res) {
            this.resetData()
            this.$emit('close')
          }
        },
        'Confirm',
        'Go Back',
        'Cancel Import',
        'This will remove the current configuration and selection.'
      )
    },
    formatBytes (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    async processFiles (files) {
      const fileArray = Array.from(files)

      for (const file of fileArray) {
        const sizeFormatted = this.formatBytes(file.size)

        this.uploadedFiles.push({
          id: Date.now() + '-' + Math.random().toString(36).substr(2, 9),
          file: file,
          name: file.name,
          size: sizeFormatted,
          headerRow: 1,
          columnsFound: 0,
          detectedColumns: [],
          isParsed: false
        })
      }
    },
    removeFile (index) {
      this.uploadedFiles.splice(index, 1)
      if (this.uploadedFiles.length === 0) {
        if (this.currentStep > 1) {
          this.currentStep = 1
        }
        this.globalMapping = {
          headerRow: 1,
          mappings: {},
          identificationCells: {},
          detectedColumns: [],
          columnsFound: 0,
          isParsed: false,
          isFullyMapped: false,
          parsedFileId: null
        }
      }
    },
    goToStep3 () {
      if (!this.globalMapping.isFullyMapped) {
        this.showValidationErrors = true
        alert('Please complete all BOM Identification and Field Mapping selections before proceeding.')
        return
      }
      this.showValidationErrors = false

      const bomItems = {}
      if (this.globalMapping.mappings) {
        for (const [key, mappingObj] of Object.entries(this.globalMapping.mappings)) {
          if (mappingObj) {
            bomItems[key] = {
              columnNumber: mappingObj.columnNumber
            }
          }
        }
      }

      let parentItemCode = null
      if (this.globalMapping.identificationCells && this.globalMapping.identificationCells.parent_item_code) {
        parentItemCode = {
          rowNumber: this.globalMapping.identificationCells.parent_item_code.row,
          columnNumber: this.globalMapping.identificationCells.parent_item_code.col + 1
        }
      }

      let erpBomId = null
      if (this.globalMapping.identificationCells && this.globalMapping.identificationCells.erp_bom_id) {
        erpBomId = {
          rowNumber: this.globalMapping.identificationCells.erp_bom_id.row,
          columnNumber: this.globalMapping.identificationCells.erp_bom_id.col + 1
        }
      }

      let bomName = null
      const useSameBomName = this.globalMapping.bomNameFromMaterial !== undefined ? this.globalMapping.bomNameFromMaterial : true
      if (!useSameBomName && this.globalMapping.identificationCells && this.globalMapping.identificationCells.bom_name) {
        bomName = {
          rowNumber: this.globalMapping.identificationCells.bom_name.row,
          columnNumber: this.globalMapping.identificationCells.bom_name.col + 1
        }
      }

      const payload = {
        bom_items: bomItems,
        parent_item_code: parentItemCode,
        erp_bom_id: erpBomId,
        bom_items_start_row: this.globalMapping.headerRow
      }

      if (bomName) {
        payload.name = bomName
      }

      this.finalMappingPayload = payload

      this.currentStep = 3
    },
    async importFiles () {
      this.isSubmitting = true

      const fileNames = this.uploadedFiles.map(f => f.file.name)

      const payload = {
        tenantId: this.openTenantId,
        feature: 'bom',
        featureId: 'bulk_import',
        fileNames: fileNames
      }

      if (this.isOnProjectLevel && this.openProjectId) {
        payload.projectId = this.openProjectId
      }

      try {
        const { url: urlEntries } = await generateS3SubmittingUrlMulti(payload)

        const urlMap = new Map((urlEntries || []).map(entry => [entry.fileName, entry.url]))

        const failedFiles = []

        const uploadPromises = this.uploadedFiles.map(async (fileObj) => {
          const url = urlMap.get(fileObj.file.name)
          if (!url) {
            console.error(`No URL returned for file: ${fileObj.file.name}`)
            failedFiles.push(fileObj.file.name)
            return null
          }

          try {
            const response = await fetch(url, {
              method: 'PUT',
              body: fileObj.file
            })

            if (!response.ok) {
              throw new Error(`Status ${response.status}`)
            }

            const blobKey = decodeURIComponent(url.split('?')[0].split('.com/').pop())

            return {
              ...fileObj,
              blobKey: blobKey
            }
          } catch (error) {
            console.error(`Error uploading ${fileObj.file.name}:`, error)
            failedFiles.push(fileObj.file.name)
            return null
          }
        })

        const results = await Promise.all(uploadPromises)
        const uploadedFilesWithKeys = results.filter(res => res !== null)

        if (uploadedFilesWithKeys.length === 0) {
          throw new Error('All file uploads failed. Import aborted.')
        }

        if (failedFiles.length > 0) {
          console.error('The following files failed to upload to S3:', failedFiles)
          alert(`${failedFiles.length} file(s) failed to upload and were skipped.`)
        }

        const blobKeys = uploadedFilesWithKeys.map(f => f.blobKey)

        const useSameBomName = this.globalMapping.bomNameFromMaterial !== undefined ? this.globalMapping.bomNameFromMaterial : true

        const finalPayload = {
          input: {
            blob_keys: blobKeys,
            mapping: this.finalMappingPayload,
            metadata: {
              bom_name_from_parent_item: useSameBomName
            }
          }
        }

        const tokenType = (this.isOnProjectLevel && this.openProjectId) ? 'project' : 'tenant'
        await BulkImportBom(finalPayload, tokenType)

        this.resetData()
        this.$emit('close')
        this.$emit('import-success')
        success('Import started successfully! Please check mail for results.')
      } catch (error) {
        console.error('Error during file upload or import:', error)
        alert(error.message || 'An error occurred during import.')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.bulk-import-content {
  font-family: "Nunito Sans", sans-serif;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 8px 0;
  width: 900px;
  max-width: 100%;
}

.import-stepper {
  display: flex;
  align-items: center;
  padding: 0 2px;
  width: 65%;
  margin: 0 auto;
}

.step {
  display: flex;
  align-items: center;
  gap: 8px;

  .step-circle {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    border-radius: 50%;
    background-color: #f3f4f6;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-xs2);
    font-weight: 600;
  }

  .step-label {
    font-size: var(--text-s);
    color: #9ca3af;
    font-weight: 500;
    white-space: nowrap;
  }

  &.active {
    .step-circle {
      background-color: #6366f1;
      color: #ffffff;
    }
    .step-label {
      color: #6366f1;
      font-weight: 600;
    }
  }

  &.completed {
    .step-circle {
      background-color: #6366f1;
      color: #ffffff;
    }
    .step-label {
      color: #6366f1;
      font-weight: 600;
    }
  }
}

.step-line {
  flex: 1;
  height: 1px;
  background-color: #e5e7eb;
  margin: 0 10px;
}

.divider {
  height: 1px;
  background-color: #e5e7eb;
  margin: 0 -10px;
}

.import-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  bottom: 0;
  background-color: var(--white, #ffffff);
  padding: 16px 10px 0 10px;
  margin: 0 -10px 0 -10px;
  border-top: 1px solid #e5e7eb;
  z-index: 3;
  transform: translateZ(0);

  .btn-cancel {
    background: none;
    border: none;
    color: #6b7280;
    font-size: var(--text-s);
    font-weight: 500;
    cursor: pointer;
    padding: 6px 10px;
    font-family: inherit;

    &.btn-back {
      font-weight: 600;
      color: #4b5563;
      display: flex;
      align-items: center;
      gap: 6px;

      .icon-left {
        width: 12px;
        height: 12px;
        opacity: 0.7;
      }
    }

    &:hover {
      color: #374151;
    }
  }

  .btn-next {
    display: flex;
    align-items: center;
    gap: 6px;
    background-color: rgba(99, 102, 241, 0.5);
    color: #ffffff;
    border: none;
    border-radius: 7px;
    padding: 8px 18px;
    font-size: var(--text-s);
    font-weight: 600;
    cursor: not-allowed;
    font-family: inherit;

    &.active {
      background-color: #6366f1;
      cursor: pointer;
    }

    &.is-loading {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .icon-right {
      width: 12px;
      height: 12px;
      filter: brightness(0) invert(1);
    }
  }
}

.spinner {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  100% { transform: rotate(360deg); }
}
</style>
